import React, { useMemo } from "react";
import { Box } from "@twilio-paste/core";
import { useNavigate } from "react-router-dom";
import Notifications from "../Notifications";
import stylesheet from "../../styles";
import AppHeader from "../AppHeader";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators, AppState } from "../../store";
import useAppAlert from "../../hooks/useAppAlerts";
import { useTwilioClient } from "../../hooks/twilio/useTwilioClient";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { markSignedOut } from "../../utils/storageUtils";
import { authClient } from "../../auth";
import { bindActionCreators } from "redux";
import NewConversationsContainer from "../new-conversations/NewConversationsContainer";
import NewConversationContainer from "../new-conversations/NewConversationContainer/NewConversationContainer";
import { Outlet } from "react-router-dom";

const AppContainer: React.FC = () => {
  const conversations = useSelector((state: AppState) => state.conversations);
  const sid = useSelector((state: AppState) => state.sid);
  const operator = useSelector((state: AppState) => state.operator);
  const roles = useSelector((state: AppState) => state.roles);
  const [alertsExist, AlertsView] = useAppAlert();
  const dispatch = useDispatch();
  const { logout } = bindActionCreators(actionCreators, dispatch);
  const { client, connectionState, usernameForHeader } = useTwilioClient();
  const navigate = useNavigate();

  const canViewAll = useMemo(() => {
    if (!operator) return false;
    const role = roles.find((r) => r.id === operator.roleId);
    return role?.canViewAll ?? false;
  }, [operator, roles]);

  useUserPreferences();

  const openedConversation = useMemo(
    () => conversations.items.find((convo) => convo.twilioSid === sid),
    [sid, conversations]
  );

  const onLogout = async () => {
    try {
      if (client) {
        client.removeAllListeners();

        if (typeof (client as any).shutdown === "function") {
          await (client as any).shutdown();
        }
      }
    } catch (e) {
      console.warn("Errore durante la disconnessione Twilio:", e);
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    } catch (e) {
      console.warn("Errore durante unregister service worker:", e);
    }
    logout();
    markSignedOut();

    const accounts = authClient.getAccounts();
    const account = accounts[0];

    if (!account) {
      window.location.reload();
      return;
    }

    if (authClient.getInProgress() !== "none") {
      console.warn("[MSAL] logout ignorato: interaction già in corso.");
      return;
    }

    await authClient.logoutRedirect({
      account: account as any,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return (
    <Box style={stylesheet.appWrapper}>
      <AlertsView />
      <Notifications />
      <Box>
        <AppHeader
          user={usernameForHeader}
          client={client}
          onSignOut={onLogout}
          connectionState={connectionState ?? "disconnected"}
          canViewAll={canViewAll}
        />
      </Box>
      <Outlet />
    </Box>
  );
};

export default AppContainer;
